import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { ApiError } from "./api";

export interface AsyncState<T> {
	data: T | null;
	error: ApiError | null;
	loading: boolean;
	reload: () => void;
}

/**
 * One request, three states, and a reload. Reloading keeps the old data on
 * screen while the new one is on its way, so a table does not flash empty
 * after every action.
 */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[]): AsyncState<T> {
	const [data, setData] = useState<T | null>(null);
	const [error, setError] = useState<ApiError | null>(null);
	const [loading, setLoading] = useState(true);
	const [tick, setTick] = useState(0);
	const generation = useRef(0);

	// biome-ignore lint/correctness/useExhaustiveDependencies: `deps` is the dependency list, by design
	useEffect(() => {
		const mine = ++generation.current;
		setLoading(true);
		setError(null);
		load().then(
			(value) => {
				if (mine !== generation.current) return;
				setData(value);
				setLoading(false);
			},
			(err) => {
				if (mine !== generation.current) return;
				setError(err instanceof ApiError ? err : new ApiError(0, "internal", "errors.internal", String(err)));
				setLoading(false);
			},
		);
	}, [...deps, tick]);

	const reload = useCallback(() => setTick((n) => n + 1), []);
	return { data, error, loading, reload };
}

/**
 * A list that grows a page at a time.
 *
 * The first page is the `useAsync` the table renders from, so reloading it
 * after an action starts the list over; every later page is fetched at its
 * offset and appended. Growing `limit` instead — which is how these tables
 * used to page — stops at the server's ceiling, and an audit log whose older
 * half cannot be reached is not an audit log. There is more to load while
 * the last page came back full.
 */
export function usePaged<R, T>(
	load: (offset: number, limit: number) => Promise<R>,
	rowsOf: (page: R) => T[],
	deps: unknown[],
	limit = 50,
): { state: AsyncState<R>; rows: T[]; hasMore: boolean; loadingMore: boolean; more: () => Promise<void> } {
	const { locale } = useI18n();
	const state = useAsync(() => load(0, limit), [...deps, limit]);
	// Later pages belong to one first page: keyed by its identity, so a
	// reload or a filter change drops them without an effect running late.
	const [extra, setExtra] = useState<{ base: R | null; pages: T[][] }>({ base: null, pages: [] });
	const [loadingMore, setLoadingMore] = useState(false);

	const first = state.data === null ? [] : rowsOf(state.data);
	const pages = state.data !== null && extra.base === state.data ? extra.pages : [];
	const rows = pages.length === 0 ? first : first.concat(...pages);
	const last = pages.length === 0 ? first : pages[pages.length - 1];
	const hasMore = state.data !== null && last.length >= limit;

	async function more() {
		const base = state.data;
		if (base === null || loadingMore) return;
		setLoadingMore(true);
		try {
			const next = rowsOf(await load(rows.length, limit));
			setExtra((prev) => ({ base, pages: prev.base === base ? [...prev.pages, next] : [next] }));
		} catch (err) {
			toast.error(err instanceof ApiError ? err.localized(locale) : String(err));
		} finally {
			setLoadingMore(false);
		}
	}

	return { state, rows, hasMore, loadingMore, more };
}

/** A value that only settles once typing has paused. */
export function useDebounced<T>(value: T, ms = 250): T {
	const [settled, setSettled] = useState(value);
	useEffect(() => {
		const handle = setTimeout(() => setSettled(value), ms);
		return () => clearTimeout(handle);
	}, [value, ms]);
	return settled;
}
